import { ListingResetError } from './listing-reset'
import type { FileMetaPart, ListingCursor } from './server-bucket-metadata-db'
import type { Bucket } from './bucket'
import type { ClientBucketMetadataDb } from './client-bucket-metadata-db'
import type { RemoteBucket } from './server-synced-bucket'
import { safe, UFile, type UFileStream } from '@w/utils'
import type { Tel } from '@w/tel'
import { nullTel } from '@w/tel'
import {
  retry,
  isRetryableError,
  type OnlineDetector,
  type RetryOptions,
} from '@w/utils'

export class AlwaysOnlineDetector implements OnlineDetector {
  online = true
  onOnlineChange(_callback: (online: boolean) => void): void {}
  async waitForOnline(): Promise<void> {}
}

export type SyncState = 'pulling' | 'synced' | 'error'

export interface CreateSyncedBucketOptions {
  bucket: Bucket
  metadataDb: ClientBucketMetadataDb
  /** Omitted: a local-only bucket. Nothing is pulled or pushed, a put resolves after the local write, and files stay unsynced. */
  remoteBucket?: RemoteBucket
  /** Current principal scopes, enumerated without retaining the directory. */
  scopes?: () => AsyncIterable<string>
  /** Principal lifetime; closed principals cannot apply late pull results. */
  signal?: AbortSignal
  tel?: Tel
  logging?: boolean
  onlineDetector?: OnlineDetector
  retryConfig?: Pick<RetryOptions, 'minTimeout' | 'maxTimeout' | 'retries'>
}

export function createSyncedBucket(options: CreateSyncedBucketOptions) {
  return new SyncedBucket(options)
}

export class SyncedBucket {
  private tel: Tel
  private logging: boolean
  private retryOp: <T>(fn: () => Promise<T>) => Promise<T>
  syncState: SyncState = 'pulling'

  constructor(options: CreateSyncedBucketOptions) {
    this.signal = options.signal
    this.scopes = options.scopes
    this.bucket = options.bucket
    this.metadataDb = options.metadataDb
    this.remoteBucket = options.remoteBucket
    this.tel = options.tel ?? nullTel
    this.logging = options.logging ?? false
    const onlineDetector = options.onlineDetector ?? new AlwaysOnlineDetector()
    this.retryOp = <T>(fn: () => Promise<T>) =>
      retry(fn, {
        retries: options.retryConfig?.retries ?? Number.POSITIVE_INFINITY,
        minTimeout: options.retryConfig?.minTimeout,
        maxTimeout: options.retryConfig?.maxTimeout ?? 60_000,
        factor: 2,
        randomize: true,
        shouldRetry: ({ error }) => !(error instanceof ListingResetError) && isRetryableError(error),
        onFailedAttempt: async () => {
          if (!onlineDetector.online) await onlineDetector.waitForOnline()
        },
      })

    this.log('syncState=pulling reason=initial')

    // Initial sync: pull from server and push unsynced local files in parallel
    // Retries handled internally by _pull and _push
    Promise.all([this._pull(), this._push()])
      .then(() => {
        this.syncState = 'synced'
        this.log('syncState=synced reason=syncComplete')
        return true
      })
      .catch(() => {
        this.syncState = 'error'
        this.log('syncState=error reason=syncFailed')
      })
  }

  private signal: AbortSignal | undefined

  private assertActive(): void { this.signal?.throwIfAborted() }

  private scopes: (() => AsyncIterable<string>) | undefined
  private bucket: Bucket
  private metadataDb: ClientBucketMetadataDb
  private remoteBucket: RemoteBucket | undefined
  /** Only reached through `_push`, `_pull` and `get`, which return before it when there is no remote. */
  private get remote(): RemoteBucket { return safe(this.remoteBucket, 'Local-only bucket has no remote') }

  private log(msg: string): void {
    if (this.logging) console.info(msg)
  }

  put(file: UFile | UFileStream): Promise<{ size: number }> {
    return this.tel.span(
      {
        name: 'synced-bucket.put',
        type: 'mutation',
        target: 'bucket',
        input: undefined,
      },
      async tel => {
        this.log(`put path=${file.path}`)
        // Until this put finishes (landed or failed) no loop may mark the path: it may hold the previous bytes.
        this.bump(file.path)
        this.publishing.set(file.path, (this.publishing.get(file.path) ?? 0) + 1)
        let result: { size: number }
        let completed: number
        let succeeded = false
        try {
          // 1. DB track first (mark as unsynced)
          const size = file instanceof UFile ? file.size : file.size
          await this.withMetadata(file.path, () => this.metadataDb.setFileMeta(file.path, {
            synced: false,
            deletedAt: null,
            size,
          }, tel))

          // 2. Local write
          // An already-issued remote file write must finish before these newer bytes land.
          await this.incoming.get(file.path)
          result = await this.bucket.put(file)
          succeeded = true
        } finally {
          const left = (this.publishing.get(file.path) ?? 1) - 1
          if (left > 0) this.publishing.set(file.path, left)
          else this.publishing.delete(file.path)
          // Success or failure: a loop that captured the generation before now must not mark.
          this.bump(file.path)
          completed = this.gens.get(file.path) ?? 0
          // A last failed put has no P7, so it drains the marks it held back here.
          if (left === 0 && !succeeded) void this._push().catch((error: unknown) => { this.tel.error(error, { operation: 'synced-bucket.push' }) })
        }

        // 3. HTTP upload → server put → bucket write
        await this._push()
        // A rejection captured at or after this put finished applies to it; any later acceptance deletes it.
        const rejected = this.rejected.get(file.path)
        if (rejected && (rejected.gen ?? 0) >= completed) throw rejected.error

        return result
      },
    )
  }

  // One push loop at a time. Every local write bumps its path's generation before it writes, and a put
  // bumps it again when it finishes, landed or failed; a loop marks a path synced only if the generation
  // it captured is still current and no put of the path is unfinished (publishing counts them).
  private pushing: Promise<void> = Promise.resolve()
  private gens = new Map<string, number>()
  private publishing = new Map<string, number>()
  private rejected = new Map<string, { gen: number | undefined; error: Error }>()
  private incoming = new Map<string, Promise<boolean>>()

  private publishIncoming(path: string, gen: number | undefined, publish: () => Promise<unknown>): Promise<boolean> {
    const previous = this.incoming.get(path) ?? Promise.resolve()
    const task = previous.then(async () => {
      if (this.gens.get(path) !== gen || this.publishing.has(path)) return false
      await publish()
      return true
    })
    // Keep failures visible to the remote caller without blocking a later local put.
    const settled = task.catch(() => false)
    this.incoming.set(path, settled)
    void settled.then(() => this.incoming.get(path) === settled && this.incoming.delete(path))
    return task
  }

  // Per-client metadata ordering: an issued remote commit finishes before a newer
  // local commit. Callbacks contain metadata work only; file and push queues stay outside.
  private metadata = new Map<string, Promise<null>>()

  private withMetadata<T>(path: string, work: () => Promise<T>): Promise<T> {
    const previous = this.metadata.get(path) ?? Promise.resolve()
    const task = previous.then(work)
    // Track settlement independently: the caller still receives the original rejection.
    const settled = task.then(() => null, () => null)
    this.metadata.set(path, settled)
    void settled.then(() => this.metadata.get(path) === settled && this.metadata.delete(path))
    return task
  }

  private async localFile(path: string, tel: Tel): Promise<UFile | null> {
    const file = await this.bucket.get(path)
    // Read deletion after file I/O, so a held retained-file read cannot bypass a newer tombstone.
    const meta = await this.withMetadata(path, () => this.metadataDb.getFileMeta(path, tel))
    return meta?.deletedAt ? null : file
  }

  private bump(path: string) {
    this.gens.set(path, (this.gens.get(path) ?? 0) + 1)
  }

  _push(): Promise<void> {
    if (!this.remoteBucket) return Promise.resolve()
    this.pushing = this.pushing.then(() => this._pushOnce(), () => this._pushOnce())
    return this.pushing
  }

  private _pushOnce(): Promise<void> {
    return this.tel.span(
      {
        name: 'synced-bucket.push',
        type: 'mutation',
        target: 'bucket',
        input: undefined,
      },
      async tel => {
        const unsyncedFiles = await this.metadataDb.getUnsyncedFiles(tel)
        this.log(`push files=${unsyncedFiles.length}`)

        for (const path of unsyncedFiles) {
          const gen = this.gens.get(path)
          const meta = await this.metadataDb.getFileMeta(path, tel)
          if (!meta) continue

          if (meta.deletedAt) {
            // Soft delete: sync deletion to server with retry
            const result = await this.retryOp(() =>
              this.remote.delete(path, { soft: true }),
            )
            if (!result.success) {
              this.reject(path, gen, result.error)
              continue
            }
          } else {
            // Put or restore: upload file to server with retry
            const file = await this.bucket.get(path)
            if (!file) continue
            const result = await this.retryOp(() => this.remote.put(file))
            if (!result.success) {
              this.reject(path, gen, result.error)
              continue
            }
          }

          // Mark as synced only if no local write happened since this loop captured the path and none is unfinished.
          this.rejected.delete(path)
          const latest = await this.metadataDb.getFileMeta(path, tel)
          await this.withMetadata(path, async () => {
            if (latest && this.gens.get(path) === gen && !this.publishing.has(path)) await this.metadataDb.setFileMeta(path, { ...latest, synced: true }, tel)
          })
        }
      },
    )
  }

  // A server rejection stays pending for its path and never stops other paths; put reports its own.
  private reject(path: string, gen: number | undefined, message: string) {
    const error = new Error(message)
    this.rejected.set(path, { gen, error })
    this.tel.error(error, { operation: 'synced-bucket.push' })
  }

  private pullTask: Promise<void> | undefined

  _pull(): Promise<void> {
    if (this.pullTask) return this.pullTask
    const task = this.drainPull().finally(() => { this.pullTask = undefined })
    this.pullTask = task
    return task
  }

  private drainPull(): Promise<void> {
    this.assertActive()
    if (!this.remoteBucket) return Promise.resolve()
    return this.tel.span(
      {
        name: 'synced-bucket.pull',
        type: 'query',
        target: 'bucket',
        input: undefined,
      },
      async tel => {
        if (this.scopes) {
          for await (const scope of this.scopes()) await this.pullScope(scope, tel)
          return
        }
        await this.pullScope('', tel)
      },
    )
  }

  private async applyRemoteFile(path: string, deletedAt: number | null, size: number, uploadedAt: number, tel: Tel): Promise<void> {
    // Listing replay preserves bytes only when their server write version matches.
    const gen = this.gens.get(path)
    const local = await this.metadataDb.getFileMeta(path, tel)
    this.assertActive()
    if (local && !local.synced) return
    if (!deletedAt && local?.uploadedAt !== uploadedAt && !await this.publishIncoming(path, gen, () => {
      this.assertActive()
      return this.bucket.delete(path)
    })) return
    await this.withMetadata(path, async () => {
      this.assertActive()
      if (this.gens.get(path) !== gen || this.publishing.has(path)) return
      await this.metadataDb.setFileMeta(path, { synced: true, deletedAt, size, uploadedAt }, tel)
    })
  }

  private async pullScope(scope: string, tel: Tel): Promise<void> {
    this.assertActive()
    const db = this.metadataDb
    const remote = this.remote
    if (!db.getScopedPullState || !db.setScopedPullState || !db.clearScopedPullState || !remote.getFilesPage || !remote.getFilePathPart) throw new Error('Scoped listing adapter required')
    let cursor = await db.getScopedPullState(scope, tel)
    for (;;) {
      this.assertActive()
      try {
        const request = { scope, cursor }
        const getPage = remote.getFilesPage.bind(remote)
        const page = await this.retryOp(() => getPage(request, { tel }))
        this.assertActive()
        for (const item of page.items) {
          const path = await this.readListingPath(item, page.cursor, tel)
          if (path !== null) await this.applyRemoteFile(path, item.deletedAt, item.size, item.uploadedAt, tel)
        }
        this.assertActive()
        await db.setScopedPullState(page.cursor, tel)
        cursor = page.cursor
        if (page.complete) return
      } catch (error) {
        if (!(error instanceof ListingResetError)) throw error
        // Clear only the affected scope. A subsequent page always rechecks current policy.
        this.assertActive()
        await db.clearScopedPullState(scope, tel)
        cursor = null
      }
    }
  }

  private async readListingPath(item: FileMetaPart, cursor: ListingCursor, tel: Tel): Promise<string | null> {
    const decoder = new TextDecoder('utf-8', { fatal: true })
    let offset = item.pathPart.byteLength
    let path = decoder.decode(item.pathPart, { stream: true })
    while (offset < item.pathBytes) {
      const request = { cursor, changeSeq: item.changeSeq, offset }
      const remote = this.remote
      if (!remote.getFilePathPart) throw new Error('Scoped listing adapter required')
      const getPart = remote.getFilePathPart.bind(remote)
      const part = await this.retryOp(() => getPart(request, { tel }))
      this.assertActive()
      // Overwritten versions are delivered under their newer sequence in the next scan.
      if (part === null) return null
      if (part.byteLength === 0 || part.byteLength > 65536 || offset + part.byteLength > item.pathBytes) throw new Error('Invalid path fragment')
      path += decoder.decode(part, { stream: true })
      offset += part.byteLength
    }
    return path + decoder.decode()
  }

  get(path: string, options?: { retry?: boolean }) {
    return this.tel.span(
      {
        name: 'synced-bucket.get',
        type: 'query',
        target: 'bucket',
        input: undefined,
      },
      async tel => {
        this.log(`get path=${path}`)

        // Check if file is soft-deleted locally
        const gen = this.gens.get(path)
        const meta = await this.metadataDb.getFileMeta(path, tel)
        if (meta?.deletedAt) return null

        // Try local first
        const localFile = await this.bucket.get(path)
        if (this.gens.get(path) !== gen) return this.localFile(path, tel)
        if (localFile) return localFile
        // A pending local write owns the path: never fetch over it.
        if (meta && !meta.synced) return null
        if (!this.remoteBucket) return null

        // Interactive readers can surface a failed attempt and offer Retry;
        // background replication and ordinary reads retain their retry policy.
        const serverFile = options?.retry === false
          ? await this.remote.get(path)
          : await this.retryOp(() => this.remote.get(path))
        if (!serverFile) return null
        if (this.gens.get(path) !== gen) return this.localFile(path, tel)

        // Mark as synced first
        await this.withMetadata(path, async () => {
          if (this.gens.get(path) !== gen || this.publishing.has(path)) return
          await this.metadataDb.setFileMeta(path, { synced: true, deletedAt: null, size: serverFile.blob.size, uploadedAt: typeof serverFile.meta?.uploadedAt === 'number' ? serverFile.meta.uploadedAt : undefined }, tel)
        })

        // Write to local
        const published = await this.publishIncoming(path, gen, () => this.bucket.put(serverFile))

        return published && this.gens.get(path) === gen ? serverFile : this.localFile(path, tel)
      },
    )
  }

  delete(path: string): Promise<void> {
    return this.tel.span(
      {
        name: 'synced-bucket.delete',
        type: 'mutation',
        target: 'bucket',
        input: undefined,
      },
      async tel => {
        this.log(`delete path=${path}`)

        const changed = await this.withMetadata(path, async () => {
          const meta = await this.metadataDb.getFileMeta(path, tel)
          if (!meta) return false
          this.bump(path)
          await this.metadataDb.setFileMeta(path, { ...meta, deletedAt: Date.now(), synced: false }, tel)
          return true
        })
        if (!changed) return

        void this._push().catch((error: unknown) => { this.tel.error(error, { operation: 'synced-bucket.push' }) })
      },
    )
  }

  restore(path: string): Promise<void> {
    return this.tel.span(
      {
        name: 'synced-bucket.restore',
        type: 'mutation',
        target: 'bucket',
        input: undefined,
      },
      async tel => {
        this.log(`restore path=${path}`)

        const changed = await this.withMetadata(path, async () => {
          const meta = await this.metadataDb.getFileMeta(path, tel)
          if (!meta?.deletedAt) return false
          this.bump(path)
          await this.metadataDb.setFileMeta(path, { ...meta, deletedAt: null, synced: false }, tel)
          return true
        })
        if (!changed) return

        void this._push().catch((error: unknown) => { this.tel.error(error, { operation: 'synced-bucket.push' }) })
      },
    )
  }

  reconcile(): Promise<void> {
    return this.tel.span(
      {
        name: 'synced-bucket.reconcile',
        type: 'mutation',
        target: 'bucket',
        input: undefined,
      },
      async tel => {
        const bucketPaths = new Set(await this.bucket.list())
        const dbPaths = await this.metadataDb.getAllFilePaths(tel)

        this.log(
          `reconcile bucketFiles=${bucketPaths.size} dbFiles=${dbPaths.length}`,
        )

        // DELETE orphaned bucket files (exist in bucket but not DB)
        for (const path of bucketPaths) {
          if (!dbPaths.includes(path)) {
            await this.bucket.delete(path)
          }
        }

        // DELETE orphaned DB rows (exist in DB but not bucket)
        for (const path of dbPaths) {
          if (!bucketPaths.has(path)) {
            await this.metadataDb.deleteFileMeta(path, tel)
          }
        }
      },
    )
  }
}
